import * as React from "react";
import { styled } from "@mui/system";
import { grey, blue } from "@mui/material/colors";
import _ from "lodash";
import api from "src/store/features/api";
import { trackEvent } from "src/Amplitude";
import { useMarketsState } from "src/store/features/markets.slice";
import { TextareaAutosize } from "@mui/material";

const CustomTextArea = styled(TextareaAutosize)(
  ({ theme }) => `
  width: 100%;
  font-family: IBM Plex Sans, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 12px;
  border-radius: 12px 12px 0 12px;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  box-shadow: 0px 2px 2px ${theme.palette.mode === "dark" ? grey[900] : grey[50]};

  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${theme.palette.mode === "dark" ? blue[500] : blue[200]};
  }

  // firefox
  &:focus-visible {
    outline: 0;
  }
`,
);

/*
 * Return isLoading true while the note is being saved
 * Uses the default styles from MUI
 */
const StyledTextArea = React.memo(
  (props: {
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    value: string | undefined;

    refetch: () => void;
  }) => {
    const { ticker } = useMarketsState();
    const { setIsLoading, value } = props;
    const [saveNote, { isLoading, isSuccess }] = api.useUpdateCoinNoteMutation();
    const [userInput, setUserInput] = React.useState("");

    React.useEffect(() => {
      setIsLoading(isLoading);
    }, [isLoading]); // Call back that will trigger "Saving..."

    React.useEffect(() => {
      if (isSuccess) {
        // Await until the database is updated
        setTimeout(() => {
          props.refetch();
        }, 1000);
      }
    }, [isSuccess]); // Call back that will trigger "Saving..."

    React.useEffect(() => {
      // Load initial note when available
      setUserInput(value ?? "");
    }, [value]);

    const debouncedSaveNote = React.useCallback(
      _.debounce(async (value) => {
        const userDeletedAll = userInput.concat(value) === "";
        const detectedChange = userInput !== value;
        if (detectedChange || userDeletedAll) {
          await saveNote({
            updateCoinDto: {
              ticker,
              value,
            },
          })
            .unwrap()
            .then((res) => {
              setUserInput(res.value);
            });
          trackEvent("User_Edited_Coin_Note", {
            ticker,
          });
        }
      }, 2000),
      [ticker], // dependencies can be added if needed
    );

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      e.stopPropagation();
      setUserInput(e.target.value);
      debouncedSaveNote(e.target.value);
    };

    return (
      <CustomTextArea
        aria-label="minimum height"
        value={userInput}
        minRows={6}
        maxRows={6}
        placeholder="Enter your notes here"
        onChange={handleChange}
      />
    );
  },
);

export default StyledTextArea;
